"use client";

import React from "react";
import { motion } from "framer-motion";
import { Cpu, Users, Bot, Check, Info, Sparkles, Laptop, ShieldCheck, Layers, Coins } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
export default function EquipmentAndGroupComparison() {
  const duploSpecs = [{
    vi: "Lắp ráp & tư duy cơ khí",
    en: "Building & mechanical thinking"
  }, {
    vi: "Khái niệm lập trình trực quan",
    en: "Visual programming concepts"
  }, {
    vi: "Không yêu cầu máy tính",
    en: "No computer required"
  }, {
    vi: "Hoạt động máy tính bảng tùy chọn",
    en: "Optional tablet activities"
  }];
  const whalesbotSpecs = [{
    vi: "Lập trình & cảm biến đa điểm",
    en: "Programming & multi-sensor logic"
  }, {
    vi: "Robotics di động & tự hành",
    en: "Mobile & autonomous robotics"
  }, {
    vi: "Chiến thuật & thực thi nhiệm vụ",
    en: "Mission strategy & execution"
  }, {
    vi: "Yêu cầu máy tính để lập trình",
    en: "Computer required for coding"
  }];
  return <section id="equipmentandgroupcomparison" data-nav="light" className="relative overflow-hidden bg-background text-foreground py-16 md:py-24">
      {/* Decorative Blueprint Background Grid */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.04] bg-[radial-gradient(ellipse_at_center,_var(--border)_1px,_transparent_1px)] bg-[size:24px_24px]" />

      {/* Lab Blueprint Corner Accents */}
      <div aria-hidden="true" className="pointer-events-none absolute top-6 left-6 text-secondary/30 font-mono text-xs hidden lg:block">
        [SYS_SPEC // HARDWARE & GROUP RATIO]
      </div>
      <div aria-hidden="true" className="pointer-events-none absolute top-6 right-6 text-secondary/30 font-mono text-xs hidden lg:block">
        LAB_VER // 2.4.0
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10 max-w-6xl">
        
        {/* 1. Header Block */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 border border-secondary/30 text-secondary text-xs font-semibold uppercase tracking-wider mb-4">
            <Cpu className="size-3.5" />
            <span>Chuẩn Thiết Bị & Mô Hình / Equipment & Model Standards</span>
          </div>
          
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground max-w-4xl mx-auto leading-tight">
            HỌC ROBOTICS BẰNG THIẾT BỊ THẬT – NHÓM HỌC NHỎ
            <span className="block text-lg sm:text-xl md:text-2xl font-normal text-muted-foreground mt-2">
              Learn Robotics with Real Equipment — Small Group Classes
            </span>
          </h2>

          {/* 1px Lime Green Spec Rail Divider */}
          <div className="relative mt-8 max-w-2xl mx-auto flex items-center justify-center">
            <div className="h-px w-full bg-secondary/50" />
            <div className="absolute size-2 rounded-full bg-secondary border-2 border-background" />
          </div>
        </motion.div>

        {/* 2. Part A: Visual Equipment Comparison */}
        <div className="mb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            
            {/* DUPLO Card */}
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            delay: 0.1,
            ease: [0.16, 1, 0.3, 1]
          }}>
              <Card className="h-full border-border bg-card shadow-sm hover:border-secondary transition-colors duration-200 overflow-hidden flex flex-col group">
                <div className="relative h-52 sm:h-60 overflow-hidden bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_robotics_kit.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_robotics_kit.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_robotics_kit.png-wvc-sizes" alt="Lego Duplo mechanical gears and building kit for kids aged 4-6" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute top-3 right-3">
                    <Badge variant="secondary" className="font-bold text-xs shadow-xs px-3 py-1">
                      4–6 YRS
                    </Badge>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-4 right-4">
                    <span className="text-xs font-mono text-secondary uppercase tracking-widest block font-semibold">
                      FOUNDATION TIER
                    </span>
                    <h3 className="text-xl font-bold text-foreground">DUPLO</h3>
                  </div>
                </div>

                <CardContent className="p-6 flex-1 flex flex-col justify-between">
                  <ul className="space-y-3 mb-6">
                    {duploSpecs.map((spec, i) => <li key={i} data-index={i} className="flex items-start gap-2.5 text-sm">
                        <div className="size-5 rounded-full bg-secondary/10 border border-secondary/30 flex items-center justify-center shrink-0 mt-0.5 text-secondary">
                          <Check className="size-3" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground leading-snug">{spec.vi}</p>
                          <p className="text-xs text-muted-foreground">{spec.en}</p>
                        </div>
                      </li>)}
                  </ul>

                  <div className="pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground font-mono">
                    <span>STATUS: ACTIVE</span>
                    <span>HARDWARE: TACTILE</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* WHALESBOT Card */}
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            delay: 0.2,
            ease: [0.16, 1, 0.3, 1]
          }}>
              <Card className="h-full border-border bg-card shadow-sm hover:border-secondary transition-colors duration-200 overflow-hidden flex flex-col group">
                <div className="relative h-52 sm:h-60 overflow-hidden bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_robotics_kit.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_robotics_kit.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_robotics_kit.png-wvc-sizes" alt="WhalesBot autonomous programmable robot with multi-sensors for kids aged 7+" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute top-3 right-3">
                    <Badge variant="default" className="bg-primary text-primary-foreground font-bold text-xs shadow-xs px-3 py-1">
                      7+ YRS
                    </Badge>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-4 right-4">
                    <span className="text-xs font-mono text-secondary uppercase tracking-widest block font-semibold">
                      ADVANCED TIER
                    </span>
                    <h3 className="text-xl font-bold text-foreground">WHALESBOT</h3>
                  </div>
                </div>

                <CardContent className="p-6 flex-1 flex flex-col justify-between">
                  <ul className="space-y-3 mb-6">
                    {whalesbotSpecs.map((spec, i) => <li key={i} data-index={i} className="flex items-start gap-2.5 text-sm">
                        <div className="size-5 rounded-full bg-secondary/10 border border-secondary/30 flex items-center justify-center shrink-0 mt-0.5 text-secondary">
                          <Check className="size-3" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground leading-snug">{spec.vi}</p>
                          <p className="text-xs text-muted-foreground">{spec.en}</p>
                        </div>
                      </li>)}
                  </ul>

                  <div className="pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground font-mono">
                    <span>STATUS: ACTIVE</span>
                    <span>HARDWARE: AUTONOMOUS</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Part A Section Message Quote */}
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.35,
          delay: 0.25
        }} className="mt-6 p-4 md:p-5 rounded-xl bg-muted/60 border border-border text-center max-w-3xl mx-auto flex items-center justify-center gap-3">
            <Sparkles className="size-5 text-secondary shrink-0 hidden sm:block" />
            <div>
              <p className="text-sm md:text-base font-semibold text-foreground">
                &ldquo;Từ tư duy kỹ thuật trực quan đến Robotics có thể lập trình.&rdquo;
              </p>
              <p className="text-xs md:text-sm text-muted-foreground">
                &ldquo;From intuitive engineering thinking to programmable Robotics.&rdquo;
              </p>
            </div>
          </motion.div>
        </div>

        {/* 3. Part B: Group-Model Comparison */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <h3 className="text-xl md:text-2xl font-bold text-foreground">
              Mô Hình Nhóm Học & Tỷ Lệ Bộ Kit
              <span className="block text-sm md:text-base font-normal text-muted-foreground mt-1">
                Group Learning Models & Student-to-Kit Ratios
              </span>
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Model 1: Ideal Model */}
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            delay: 0.1
          }}>
              <Card className="border-2 border-secondary/80 bg-card p-6 rounded-xl shadow-sm relative overflow-hidden h-full flex flex-col justify-between">
                <div className="absolute top-0 right-0 bg-secondary text-secondary-foreground text-[10px] font-bold font-mono px-3 py-1 rounded-bl-lg uppercase tracking-wider">
                  IDEAL MODEL / LÝ TƯỞNG
                </div>
                
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="size-10 rounded-lg bg-secondary/15 flex items-center justify-center text-secondary border border-secondary/30">
                      <Users className="size-5" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-foreground">2 HỌC SINH / 1 BỘ KIT</h4>
                      <p className="text-xs text-muted-foreground font-mono">2 STUDENTS PER KIT</p>
                    </div>
                  </div>

                  <Separator className="my-4" />

                  <p className="text-sm text-muted-foreground mb-4">
                    Tối đa thời gian thực hành cá nhân, chia sẻ vai trò xây dựng và lập trình đồng đều, đảm bảo mỗi học sinh nắm chắc 100% thao tác kỹ thuật.
                  </p>
                </div>

                <div className="flex items-center gap-2 text-xs text-secondary font-semibold pt-2">
                  <ShieldCheck className="size-4" />
                  <span>Tương tác tối đa &amp; Thực hành chuyên sâu</span>
                </div>
              </Card>
            </motion.div>

            {/* Model 2: Cost-Optimized Model */}
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            delay: 0.2
          }}>
              <Card className="border border-border bg-card p-6 rounded-xl shadow-sm relative overflow-hidden h-full flex flex-col justify-between">
                <div className="absolute top-0 right-0 bg-muted text-muted-foreground text-[10px] font-bold font-mono px-3 py-1 rounded-bl-lg uppercase tracking-wider">
                  COST-OPTIMIZED MODEL / TỐI ƯU CHI PHÍ
                </div>

                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="size-10 rounded-lg bg-muted flex items-center justify-center text-muted-foreground border border-border">
                      <Users className="size-5" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-foreground">3 HỌC SINH / 1 BỘ KIT</h4>
                      <p className="text-xs text-muted-foreground font-mono">3 STUDENTS PER KIT</p>
                    </div>
                  </div>

                  <Separator className="my-4" />

                  <p className="text-sm text-muted-foreground mb-4">
                    Lựa chọn tiết kiệm chi phí với sự phân chia vai trò rõ ràng (Lắp ráp, Lập trình, Kiểm thử). Phù hợp cho hoạt động nhóm phối hợp.
                  </p>
                </div>

                <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2 font-mono">
                  <Layers className="size-4" />
                  <span>Phân công vai trò &amp; Tiết kiệm chi phí</span>
                </div>
              </Card>
            </motion.div>
          </div>

          {/* Part B Supporting Quote */}
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.35,
          delay: 0.25
        }} className="mt-6 p-4 md:p-5 rounded-xl bg-card border border-border shadow-2xs text-center max-w-4xl mx-auto">
            <p className="text-sm md:text-base font-medium text-foreground">
              &ldquo;Học viên được trực tiếp lắp ráp, thử nghiệm, lập trình và điều khiển robot thay vì chỉ quan sát giáo viên.&rdquo;
            </p>
            <p className="text-xs md:text-sm text-muted-foreground mt-1">
              &ldquo;Students directly assemble, experiment, program and control robots rather than just observe the instructor.&rdquo;
            </p>
          </motion.div>
        </div>

        {/* 4. Part C: Equipment Cost Reference Block */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.35,
        delay: 0.1
      }}>
          <Card className="border border-border bg-card/80 backdrop-blur-sm rounded-xl p-6 md:p-8 shadow-sm relative overflow-hidden">
            {/* Blueprint grid subtle detail */}
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-5 bg-[linear-gradient(to_right,#888_1px,transparent_1px),linear-gradient(to_bottom,#888_1px,transparent_1px)] bg-[size:16px_16px]" />

            <div className="relative z-10">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="size-9 rounded-lg bg-secondary/15 border border-secondary/30 text-secondary flex items-center justify-center shrink-0">
                    <Coins className="size-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold tracking-tight text-foreground uppercase">
                      CHI PHÍ THIẾT BỊ THAM KHẢO
                    </h3>
                    <p className="text-xs font-mono text-muted-foreground uppercase">
                      REFERENCE EQUIPMENT COST
                    </p>
                  </div>
                </div>

                <Badge variant="outline" className="text-[11px] font-mono text-muted-foreground w-fit">
                  THAM KHẢO THIẾT BỊ (KHÔNG PHẢI HỌC PHÍ)
                </Badge>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                
                {/* Duplo Price Item */}
                <div className="p-4 rounded-lg bg-background border border-border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold font-mono text-secondary uppercase">
                      DUPLO KIT (4–6 YRS)
                    </span>
                    <Badge variant="secondary" className="text-[10px]">BASIC</Badge>
                  </div>
                  <p className="text-xl md:text-2xl font-bold text-foreground">
                    Khoảng 1.000.000 VNĐ <span className="text-sm font-normal text-muted-foreground">/ bộ</span>
                  </p>
                </div>

                {/* WhalesBot Price Item */}
                <div className="p-4 rounded-lg bg-background border border-border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold font-mono text-primary uppercase">
                      WHALESBOT KIT (7+ YRS)
                    </span>
                    <Badge variant="default" className="text-[10px] bg-primary">ADVANCED</Badge>
                  </div>
                  <p className="text-xl md:text-2xl font-bold text-foreground">
                    Khoảng 6.500.000 VNĐ <span className="text-sm font-normal text-muted-foreground">/ bộ theo mức tham khảo hiện tại</span>
                  </p>
                </div>
              </div>

              {/* Cost Footnote Note */}
              <div className="flex items-start gap-2.5 p-3 rounded-lg bg-muted/50 border border-border text-xs text-muted-foreground">
                <Info className="size-4 text-secondary shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">
                    Chi phí thiết bị có thể thay đổi tùy thời điểm, cấu hình và chính sách cung cấp.
                  </p>
                  <p className="text-[11px]">
                    Equipment costs may vary based on timing, configuration, and supply policies.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

      </div>
    </section>;
}